import { useMutation, useQuery } from '@apollo/client/react'
import { Card, ContentLayout } from '@faceup/ui'
import { Button, Checkbox, ModalConfirm, Popconfirm, Table, useMessage } from '@faceup/ui-base'
import { useState } from 'react'
import { graphql } from '../__generated__'
import { ResyncableGlobalTable } from '../__generated__/graphql'
import { ForcedLegacyInvestigationMigrationCard } from '../Components/ForcedLegacyInvestigationMigrationCard'
import { RouteAiHotlineNumbersCard } from '../Components/RouteAiHotlineNumbersCard'
import { TrpcProvider } from '../TrpcProvider'
import { DEFAULT_SUPPORTED_REGION, useRegion } from '../utils/useRegion'

const query = {
  GlobalTablesQuery: graphql(`
    query GlobalTablesQuery {
      globalTables
    }
  `),
}

const mutations = {
  truncateGlobalTable: graphql(`
    mutation TruncateGlobalTableMutation($input: TruncateGlobalTableInput!) {
      truncateGlobalTable(input: $input) {
        success
      }
    }
  `),
  resyncGlobalTable: graphql(`
    mutation ResyncGlobalTableMutation($input: ResyncGlobalTableInput!) {
      resyncGlobalTable(input: $input) {
        syncedCount
      }
    }
  `),
}

// Tables the report source UUID migration touches. Each region rebuilds its own share after the
// deploy; the truncate option is only offered for the DynamoDB tables, the school mirror lives in
// Postgres and is only re-sent to the other regions.
const resyncableTables: Record<
  ResyncableGlobalTable,
  { description: string; canTruncate: boolean }
> = {
  [ResyncableGlobalTable.ShortlinkDiscovery]: {
    description:
      'Rebuilds the shortlink items of this region from its report sources and removes orphaned items of this region.',
    canTruncate: true,
  },
  [ResyncableGlobalTable.ReportPasswordDiscovery]: {
    description:
      'Rebuilds the report password items of this region from its report sources and removes orphaned items of this region.',
    canTruncate: true,
  },
  [ResyncableGlobalTable.PublicSchoolDiscovery]: {
    description:
      'Re-sends the public school rows of this region to the other regions (Postgres mirror).',
    canTruncate: false,
  },
}

export const RegionMaintenanceView = () => {
  const message = useMessage()
  const [tableToResync, setTableToResync] = useState<ResyncableGlobalTable | null>(null)
  const [truncateFirst, setTruncateFirst] = useState(false)
  const { region } = useRegion()
  const { loading, data } = useQuery(query.GlobalTablesQuery, {
    fetchPolicy: 'cache-and-network',
  })

  const [truncateGlobalTable] = useMutation(mutations.truncateGlobalTable, {
    onCompleted: data => {
      if (data?.truncateGlobalTable?.success) {
        message.success('Table truncated')
      } else {
        message.error('Error truncating table')
      }
    },
  })

  const [resyncGlobalTable, { loading: resyncing }] = useMutation(mutations.resyncGlobalTable, {
    onCompleted: data => {
      message.success(`Resynced ${data.resyncGlobalTable?.syncedCount ?? 0} rows of this region`)
    },
    onError: () => {
      message.error('Error resyncing table')
    },
  })

  const globalTables = data?.globalTables?.map(table => ({
    table,
    truncateTable: () => truncateGlobalTable({ variables: { input: { table } } }),
  }))

  const closeResyncModal = () => {
    setTableToResync(null)
    setTruncateFirst(false)
  }

  const confirmResync = async () => {
    if (!tableToResync) {
      return
    }
    await resyncGlobalTable({
      variables: { input: { table: tableToResync, truncateFirst } },
    })
    closeResyncModal()
  }

  return (
    <ContentLayout header={<ContentLayout.Header title='Region maintenance' />}>
      <Table
        shadow
        loading={loading}
        columns={[
          {
            title: 'Table name',
            dataIndex: 'table',
            width: '80%',
          },
          {
            title: 'Actions',
            width: '20%',
            render: ({ truncateTable }) => (
              <div className='flex justify-end'>
                <Popconfirm
                  title='Truncating global table will delete all data across all regions. Are you sure?'
                  onConfirm={() => truncateTable()}
                  placement='topRight'
                >
                  <Button ghost danger>
                    Truncate table
                  </Button>
                </Popconfirm>
              </div>
            ),
          },
        ]}
        rowKey='table'
        dataSource={globalTables ?? []}
        pagination={false}
        className='min-h-[300px]'
        scroll={{ x: 'max-content' }}
      />

      <Card className='mt-6'>
        <Card.Header>Resync after the report source UUID migration</Card.Header>
        <Card.Content>
          <p className='mb-4 text-colorTextSecondary'>
            Run once in every region after the deploy. Each action rebuilds only the rows of the
            region you are connected to.
          </p>
          <Table
            columns={[
              {
                title: 'Table name',
                dataIndex: 'table',
                width: '30%',
              },
              {
                title: 'What it does',
                width: '50%',
                render: ({ description }) => description,
              },
              {
                title: 'Actions',
                width: '20%',
                render: ({ table }) => (
                  <div className='flex justify-end'>
                    <Button ghost type='primary' onClick={() => setTableToResync(table)}>
                      Resync
                    </Button>
                  </div>
                ),
              },
            ]}
            rowKey='table'
            dataSource={Object.values(ResyncableGlobalTable).map(table => ({
              table,
              ...resyncableTables[table],
            }))}
            pagination={false}
            scroll={{ x: 'max-content' }}
          />
        </Card.Content>
      </Card>

      <div className='mt-6 flex flex-col gap-6'>
        <TrpcProvider region={region ?? DEFAULT_SUPPORTED_REGION}>
          <RouteAiHotlineNumbersCard />
          <ForcedLegacyInvestigationMigrationCard />
        </TrpcProvider>
      </div>

      <ModalConfirm
        open={tableToResync !== null}
        title={`Resync ${tableToResync ?? ''}`}
        confirmText='Resync'
        confirmState={{ loading: resyncing, danger: truncateFirst }}
        onConfirm={confirmResync}
        onCancel={closeResyncModal}
      >
        <div className='flex flex-col gap-3'>
          <p>{tableToResync ? resyncableTables[tableToResync].description : null}</p>
          {tableToResync && resyncableTables[tableToResync].canTruncate && (
            <Checkbox
              checked={truncateFirst}
              onChange={event => setTruncateFirst(event.target.checked)}
            >
              Truncate the table first. This deletes the data of <strong>all regions</strong>; the
              other regions stay empty until they run their own resync.
            </Checkbox>
          )}
        </div>
      </ModalConfirm>
    </ContentLayout>
  )
}
